import { yieldToMainThread } from '@theme/utilities';
import { Component } from '@theme/component';

/**
 * Finds the active cart icon element in the header or DOM
 * @returns {Element|null}
 */
function findCartElement() {
  const selectors = [
    'cart-icon',
    '.header-actions__cart-icon',
    '[data-testid="cart-icon"]',
    'header-actions cart-icon',
    'header-actions .header-actions__cart-icon',
    'a[href*="/cart"]'
  ];

  for (const sel of selectors) {
    const el = document.querySelector(sel);
    if (el) {
      const rect = el.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) {
        return el;
      }
    }
  }
  return document.querySelector('cart-icon') || document.querySelector('.header-actions__cart-icon');
}

/**
 * Global helper to trigger fly-to-cart animation directly with Web Animations API
 * @param {Element} source - The source card, image, or button
 * @param {string} [imageUrl] - Optional product thumbnail image URL
 * @param {Element} [destination] - Optional destination cart element
 */
export function flyProductToCart(source, imageUrl, destination) {
  if (!source) return;

  // 1. Get Source bounding rect
  let startX = window.innerWidth / 2;
  let startY = window.innerHeight / 2;

  let imgEl = null;
  if (source instanceof HTMLElement) {
    imgEl = source.querySelector('img') || (source.tagName === 'IMG' ? source : null);
  }

  const targetSource = imgEl || source;
  const sRect = targetSource.getBoundingClientRect();

  if (sRect && (sRect.width > 0 || sRect.height > 0)) {
    startX = sRect.left + sRect.width / 2;
    startY = sRect.top + sRect.height / 2;
  }

  // 2. Get Destination (Cart icon) bounding rect
  const cartEl = destination || findCartElement();
  let endX = window.innerWidth - 50;
  let endY = 40;

  if (cartEl) {
    const dRect = cartEl.getBoundingClientRect();
    if (dRect.width > 0 || dRect.height > 0) {
      endX = dRect.left + dRect.width / 2;
      endY = dRect.top + dRect.height / 2;
    }
  }

  // 3. Fallback image if not provided
  let bgImage = imageUrl;
  if (!bgImage && imgEl && imgEl.currentSrc) {
    bgImage = imgEl.currentSrc;
  } else if (!bgImage && imgEl && imgEl.src) {
    bgImage = imgEl.src;
  }

  // 4. Create flying ghost element
  const flyer = document.createElement('div');
  flyer.className = 'fly-to-cart-flying-item';

  if (bgImage) {
    flyer.style.backgroundImage = `url("${bgImage}")`;
    flyer.style.backgroundSize = 'cover';
    flyer.style.backgroundPosition = 'center';
  } else {
    flyer.style.backgroundColor = '#5a684a';
  }

  document.body.appendChild(flyer);

  // 5. Parabolic arc computation
  // Midpoint arches upwards towards top-right
  const dx = endX - startX;
  const dy = endY - startY;
  const midX = startX + dx * 0.45;
  const midY = Math.min(startY, endY) - Math.max(50, Math.min(180, Math.abs(dx) * 0.22));

  const keyframes = [
    {
      left: `${startX}px`,
      top: `${startY}px`,
      transform: 'translate(-50%, -50%) scale(1) rotate(0deg)',
      opacity: 0.95,
      borderRadius: '12px'
    },
    {
      offset: 0.25,
      left: `${startX + dx * 0.2}px`,
      top: `${startY - 30}px`,
      transform: 'translate(-50%, -50%) scale(1.15) rotate(-8deg)',
      opacity: 1,
      borderRadius: '16px'
    },
    {
      offset: 0.6,
      left: `${midX}px`,
      top: `${midY}px`,
      transform: 'translate(-50%, -50%) scale(0.85) rotate(6deg)',
      opacity: 0.9,
      borderRadius: '24px'
    },
    {
      offset: 0.88,
      left: `${startX + dx * 0.9}px`,
      top: `${endY - 8}px`,
      transform: 'translate(-50%, -50%) scale(0.38) rotate(14deg)',
      opacity: 0.85,
      borderRadius: '50%'
    },
    {
      left: `${endX}px`,
      top: `${endY}px`,
      transform: 'translate(-50%, -50%) scale(0.12) rotate(22deg)',
      opacity: 0,
      borderRadius: '50%'
    }
  ];

  const anim = flyer.animate(keyframes, {
    duration: 720,
    easing: 'cubic-bezier(0.2, 0.9, 0.3, 1)',
    fill: 'forwards'
  });

  anim.onfinish = () => {
    flyer.remove();

    // Trigger bounce on cart icon
    if (cartEl) {
      cartEl.classList.remove('cart-icon--bounce');
      void cartEl.offsetWidth; // Force reflow
      cartEl.classList.add('cart-icon--bounce');
      setTimeout(() => {
        cartEl.classList.remove('cart-icon--bounce');
      }, 600);
    }
  };
}

/**
 * FlyToCart custom element for compatibility with theme components
 */
class FlyToCart extends Component {
  /** @type {Element} */
  source;

  /** @type {boolean} */
  useSourceSize = false;

  /** @type {Element} */
  destination;

  connectedCallback() {
    super.connectedCallback();
    const src = this.source || this;
    const dest = this.destination || findCartElement();
    flyProductToCart(src, null, dest);
    this.remove();
  }
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}

if (typeof window !== 'undefined') {
  window.flyProductToCart = flyProductToCart;
}
